import React, { useState, useRef, useEffect } from "react";
import AnimatedCounter from "../ui/AnimatedCounter";
import { ReactComponent as PinSelectedIcon } from "../../assets/icons/svg/Pin_selected.svg";
import { ReactComponent as PinUnSelectedIcon } from "../../assets/icons/svg/Pin_unselected.svg";
import Lottie from "lottie-react"; // ✅ NEW IMPORT
import DotLoader from "../../assets/Animation/Dot Loader.json"; // ✅ NEW IMPORT
import { DEFAULT_FONT_FAMILY } from "../../config/fontConfig";

interface ViewModeDropdownProps {
  totalFeedbackItems: number;
  hasAddedFeedback: boolean;
  onViewList: () => void;
  onShare: () => void;
  isSharing?: boolean;
  compact?: boolean;
}

type ViewMode = "view-list" | "share";

const ViewModeDropdown: React.FC<ViewModeDropdownProps> = ({
  totalFeedbackItems,
  hasAddedFeedback,
  onViewList,
  onShare,
  isSharing = false,
  compact = false,
}) => {
  // Load saved preference from localStorage, default to 'view-list' if not found
  const [selectedViewMode, setSelectedViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem("viewmode_preference");
    return (saved as ViewMode) || "view-list";
  });
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    // Close when focus moves into an iframe (clicks inside iframe don't bubble to parent document)
    const handleWindowBlur = () => {
      setShowDropdown(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("blur", handleWindowBlur);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, []);

  const handleMainButtonClick = () => {
    if (selectedViewMode === "view-list") {
      onViewList();
    } else {
      onShare();
    }
  };

  // Handle pin selection - only updates which option is pinned
  const handlePinViewMode = (mode: ViewMode, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent button click
    setSelectedViewMode(mode);
    // Save to localStorage to persist across sessions
    localStorage.setItem("viewmode_preference", mode);
    console.log(`📌 Pinned mode: ${mode} (saved to localStorage)`);
  };

  // Handle dropdown option click - immediately executes action
  const handleDropdownOptionClick = (mode: ViewMode) => {
    // Immediately execute the action when dropdown option is clicked
    setShowDropdown(false);

    if (mode === "view-list") {
      onViewList();
    } else {
      onShare();
    }
  };

  return (
    <div className="relative" ref={dropdownRef} data-tour="view-list">
      <div
        className={`flex items-center overflow-hidden ${compact ? "rounded-[4px]" : "rounded-[6px]"} ${
          !hasAddedFeedback ? "bg-[#A8C6DD]" : "bg-[#53AEF4]"
        }`}
      >
        {/* Main Button - Shows selected option with Lottie animation support */}
        <button
          onClick={handleMainButtonClick}
          disabled={!hasAddedFeedback}
          className={`inline-flex items-center justify-center transition-colors ${
            compact
              ? "px-3 py-2 text-xs"
              : "px-2 sm:px-3 xl:px-4 2xl:px-5 4xl:px-6 py-1 sm:py-1.5 xl:py-2 2xl:py-2.5 4xl:py-3 text-xs sm:text-sm xl:text-sm 2xl:text-base 4xl:text-xl"
          } ${
            !hasAddedFeedback
              ? "text-white cursor-not-allowed"
              : "text-white"
          }`}
          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
        >
          {/* ✅ NEW: Show Lottie animation when sharing */}
          {isSharing ? (
            <span
              className="inline-block relative"
              style={{ width: 60, height: 20, }}
            >
              <Lottie
                animationData={DotLoader}
                loop={true}
                style={{
                  width: 120,
                  height: 48,
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            </span>
          ) : selectedViewMode === "view-list" ? (
            <>View list (<span style={{ display: 'inline-flex', alignItems: 'center', fontSize: '0.8em', fontFamily: 'Inter, sans-serif' }}><AnimatedCounter value={totalFeedbackItems} /></span>)</>
          ) : (
            <>Share (<span style={{ display: 'inline-flex', alignItems: 'center', fontSize: '0.8em', fontFamily: 'Inter, sans-serif' }}><AnimatedCounter value={totalFeedbackItems} /></span>)</>
          )}
        </button>

        {/* Separator */}
        <div
          className={`w-px h-5 xl:h-6 2xl:h-7 4xl:h-8 ${
            !hasAddedFeedback ? "bg-white opacity-50" : "bg-[#A3D8FF] opacity-35"
          }`}
        ></div>

        {/* Dropdown Toggle Button */}
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={!hasAddedFeedback || isSharing} // ✅ UPDATED: Disable during sharing
          className={`inline-flex items-center justify-center transition-colors ${
            compact ? "px-2 py-1.5 self-stretch" : "px-2 xl:px-2.5 2xl:px-3 4xl:px-4 self-stretch"
          } ${!hasAddedFeedback || isSharing ? "cursor-not-allowed" : ""} text-white`}
        >
          <svg
            className={`w-3 h-3 xl:w-4 xl:h-4 2xl:w-4 2xl:h-4 4xl:w-5 4xl:h-5 transition-transform ${
              showDropdown ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      {/* Dropdown Menu */}
      {showDropdown && hasAddedFeedback && (
        <div className="absolute right-0 top-full mt-2 w-48 p-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
          {/* View List Option */}
          <button
            onClick={() => handleDropdownOptionClick("view-list")}
            className={`w-full px-4 py-3 text-left text-sm font-medium flex items-center justify-between rounded-lg gap-3 transition-colors ${
              selectedViewMode === "view-list"
                ? "bg-gray-100 text-black"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center flex-1">
              <span style={{ fontFamily: DEFAULT_FONT_FAMILY }}>
                View list ({totalFeedbackItems})
              </span>
            </div>
            {/* Separate pin button */}
            <button
              onClick={(e) => handlePinViewMode("view-list", e)}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
              title={
                selectedViewMode === "view-list" ? "Pinned" : "Pin this option"
              }
            >
              {selectedViewMode === "view-list" ? (
                <PinSelectedIcon className="w-4 h-4" />
              ) : (
                <PinUnSelectedIcon className="w-4 h-4" />
              )}
            </button>
          </button>

          {/* Share Option */}
          <button
            onClick={() => handleDropdownOptionClick("share")}
            className={`w-full px-4 py-3 text-left text-sm font-medium flex items-center justify-between rounded-lg gap-3 transition-colors ${
              selectedViewMode === "share"
                ? "bg-gray-100 text-black"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center flex-1">
              <span style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Share ({totalFeedbackItems})</span>
            </div>
            {/* Separate pin button */}
            <button
              onClick={(e) => handlePinViewMode("share", e)}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
              title={
                selectedViewMode === "share" ? "Pinned" : "Pin this option"
              }
            >
              {selectedViewMode === "share" ? (
                <PinSelectedIcon className="w-4 h-4" />
              ) : (
                <PinUnSelectedIcon className="w-4 h-4" />
              )}
            </button>
          </button>
        </div>
      )}
    </div>
  );
};

export default ViewModeDropdown;
